<template>
  <div
    :tabindex="tabindex"
    :class="{ 'multiselect--active': isOpen, 'multiselect--disabled': disabled, 'multiselect--above': isAbove }"
    class="multiselect"
  >
    <slot name="carret">
      <div
        class="multiselect__select"
        @mousedown.prevent.stop="toggle()"
      />
    </slot>
    <slot
      name="clear"
      :search="search"
    />
    <div class="multiselect_tags-wrap">
      <div
        ref="tags"
        tabindex="20"
        class="multiselect__tags"
        @blur.prevent="deactivate()"
        @focus.prevent="activate($event)"
      >
        <div
          v-show="visibleValue.length > 0"
          class="multiselect__tags-wrap"
        >
          <template
            v-for="option of visibleValue"
          >
            <slot
              name="tag"
              :option="option"
              :search="search"
              :remove="removeElement"
            >
              <span
                :key="`multiselect_${typeof option === 'object' ? option.vueKey : option}`"
                class="multiselect__tag"
                :class="{'remove-disabled': option && option.removeDisabled === true}"
                @mousedown.prevent
              >
                <span v-text="getOptionLabel(option, true)" />
                <i
                  v-if="!(option && option.removeDisabled === true)"
                  aria-hidden="true"
                  tabindex="1"
                  class="multiselect__tag-icon fa fa-remove"
                  @keydown.enter.prevent="removeElement(option)"
                  @mousedown.prevent="removeElement(option)"
                />
              </span>
            </slot>
          </template>
        </div>
        <template v-if="internalValue && internalValue.length > limit">
          <strong
            class="multiselect__strong"
            v-text="limitText(internalValue.length - limit)"
          />
        </template>
        <transition name="multiselect__loading">
          <slot name="loading">
            <div
              v-show="loading"
              class="multiselect__spinner"
            />
          </slot>
        </transition>
        <input
          v-if="searchable"
          :id="id"
          ref="search"
          v-fusioninputcheck="{type: 'string', tip: $t('hint.inputTip.string'), showTip, required, pattern: fusionInputCheckPattern}"
          :name="name"
          type="text"
          autocomplete="off"
          :placeholder="placeholder"
          :style="inputStyle"
          :value="searchInputBinding"
          :disabled="disabled"
          :class="['multiselect__input',extendClass]"
          @input="updateSearch($event.target.value)"
          @focus.prevent="activate($event)"
          @blur.prevent="deactivate()"
          @keyup.esc="deactivate()"
          @keydown.down.prevent="pointerForward()"
          @keydown.up.prevent="pointerBackward()"
          @keydown.enter.prevent.stop.self="addPointerElement($event)"
          @keydown.delete.stop="removeLastElement()"
        >
        <span
          v-if="!searchable && !multiple"
          class="multiselect__single"
          :title="currentOptionLabel"
          v-text="currentOptionLabel"
        />
      </div>
    </div>
    <transition name="multiselect">
      <div
        v-if="isOpen"
        ref="list"
        class="multiselect__content-wrapper"
        :class="{'no-element-wrapper': (showNoResults && (filteredOptions.length === 0 && search && !loading)), 'has-data-wrapper': filteredOptions.length && search}"
        :style="{ maxHeight: optimizedHeight + 'px' }"
        @focus="activate($event)"
        @mousedown.prevent
      >
        <ul
          class="multiselect__content"
          :style="contentStyle"
        >
          <slot name="beforeList" />
          <li v-if="multiple && max === internalValue.length">
            <span class="multiselect__option">
              <slot name="maxElements">Maximum of {{ max }} options selected. First remove a selected option to select another.</slot>
            </span>
          </li>
          <template v-if="!max || internalValue.length < max">
            <li
              v-for="(option, index) of filteredOptions"
              :key="index"
              class="multiselect__element"
            >
              <span
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="optionHighlight(index, option)"
                :data-select="option && option.isTag ? tagPlaceholder : selectLabelText"
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                class="multiselect__option"
                @click.stop="select(option)"
                @mouseenter.self="pointerSet(index)"
              >
                <slot
                  name="option"
                  :option="option"
                  :search="search"
                >
                  <span :title="getOptionLabel(option)">{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
              <span
                v-if="option && (option.$isLabel || option.$isDisabled)"
                :class="optionHighlight(index, option)"
                class="multiselect__option multiselect__option--disabled"
              >
                <slot
                  name="option"
                  :option="option"
                  :search="search"
                >
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
            </li>
          </template>
          <li v-show="showNoResults && (filteredOptions.length === 0 && search && !loading)">
            <span class="multiselect__option">
              <slot name="noResult">{{ $t('hint.noElement') }}</slot>
            </span>
          </li>
          <slot name="afterList" />
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import multiselectMixin from './multiselectMixin'
import pointerMixin from './pointerMixin'
import { utils as inputChangeUtils } from '@/plugin/InputChange'
import { getRandomIdSuffix } from '@/utils/vueCompHelper'

export default {
  name: 'VueMultiselect',
  mixins: [multiselectMixin, pointerMixin],
  props: {

    /**
       * name attribute to match optional label element
       * @default ''
       * @type {String}
       */
    name: {
      type: String,
      default: '',
    },
    /**
       * String to show when pointing to an option
       * @default 'Press enter to select'
       * @type {String}
       */
    selectLabel: {
      type: String,
      default () {
        return this.$t('hint.multiSelect.selectLabel')
      },
    },
    /**
       * String to show next to selected option
       * @default 'Selected'
       * @type {String}
      */
    selectedLabel: {
      type: String,
      default: 'Selected',
    },
    /**
       * String to show when pointing to an alredy selected option
       * @default 'Press enter to remove'
       * @type {String}
      */
    deselectLabel: {
      type: String,
      default: 'Press enter to remove',
    },
    /**
       * Decide whether to show pointer labels
       * @default true
       * @type {Boolean}
      */
    showLabels: {
      type: Boolean,
      default: true,
    },
    /**
       * Limit the display of selected options. The rest will be hidden within the limitText string.
       * @default 'label'
       * @type {String}
       */
    limit: {
      type: Number,
      default: 99999,
    },
    /**
       * Sets maxHeight style value of the dropdown
       * @default 300
       * @type {Integer}
       */
    maxHeight: {
      type: Number,
      default: 300,
    },
    /**
       * Function that process the message shown when selected
       * elements pass the defined limit.
       * @default 'and * more'
       * @param {Int} count Number of elements more than limit
       * @type {Function}
       */
    limitText: {
      type: Function,
      default: count => `and ${count} more`,
    },
    /**
       * Set true to trigger the loading spinner.
       * @default False
       * @type {Boolean}
      */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
       * Disables the multiselect if true.
       * @default false
       * @type {Boolean}
      */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
       * Required a value must be selected if true
       * @default false
       * @type {Boolean}
      */
    required: {
      type: Boolean,
      default: false,
    },
    /**
       * Fixed opening direction
       * @default ''
       * @type {String}
      */
    openDirection: {
      type: String,
      default: '',
    },
    showNoResults: {
      type: Boolean,
      default: true,
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    extendClass: {
      type: String,
      default: '',
    },
    dirtyCheckNode: String,
    dirtyCheck: {
      type: Boolean,
      default: false,
    },
    showTip: {
      type: Boolean,
      default: true,
    },
    fusionInputCheckPattern: {
      type: Function,
      default: () => true,
    },
  },
  computed: {
    visibleValue () {
      let result = this.multiple
        ? this.internalValue.slice(0, this.limit)
        : []
      return result.map(item => {
        if (typeof item === 'object') item.vueKey = getRandomIdSuffix()
        return item
      })
    },
    deselectLabelText () {
      return this.showLabels
        ? this.deselectLabel
        : ''
    },
    selectLabelText () {
      return this.showLabels
        ? this.selectLabel
        : ''
    },
    selectedLabelText () {
      return this.showLabels
        ? this.selectedLabel
        : ''
    },
    inputStyle () {
      if (this.multiple && this.value && this.value.length) {
        return this.isOpen ? {} : { 'display': 'none' }
      }
    },
    contentStyle () {
      return this.options.length
        ? { 'display': 'inline-block' }
        : { 'display': 'block' }
    },
    isAbove () {
      if (this.openDirection === 'above' || this.openDirection === 'top') {
        return true
      } else if (this.openDirection === 'below' || this.openDirection === 'bottom') {
        return false
      } else {
        return this.prefferedOpenDirection === 'above'
      }
    },
  },
  mounted () {
    if (this.dirtyCheck) {
      this.$on('input', () => {
        inputChangeUtils.setDirty(this.dirtyCheckNode)
      })
    }
  },
  destroyed () {
    if (this.dirtyCheck) {
      this.$off('input')
    }
  },
}
</script>

<style lang="scss" scoped>
fieldset[disabled] .multiselect {
  pointer-events: none;
}

.multiselect__spinner {
  position: absolute;
  right: 1px;
  top: 1px;
  width: 48px;
  height: 35px;
  background: #fff;
  display: block;
}

.multiselect__spinner:before,
.multiselect__spinner:after {
  position: absolute;
  content: "";
  top: 50%;
  left: 50%;
  margin: -8px 0 0 -8px;
  width: 16px;
  height: 16px;
  border-radius: 100%;
  border-color: #41B883 transparent transparent;
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 0 1px transparent;
}

.multiselect__spinner:before {
  animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
  animation-iteration-count: infinite;
}

.multiselect__spinner:after {
  animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
  animation-iteration-count: infinite;
}

.multiselect__loading-enter-active,
.multiselect__loading-leave-active {
  transition: opacity 0.4s ease-in-out;
  opacity: 1;
}

.multiselect__loading-enter,
.multiselect__loading-leave-active {
  opacity: 0;
}

.multiselect,
.multiselect__input,
.multiselect__single {
  font-family: inherit;
  font-size: 14px;
  touch-action: manipulation;
}

.multiselect {
  box-sizing: border-box;
  display: block;
  position: relative;
  width: 100%;
  min-width: 100px;
  min-height: 40px;
  text-align: left;
  color: #35495E;
  border: solid 1px #B5B5B5;
  background: #fff;
  cursor: pointer;
}

.multiselect * {
  box-sizing: border-box;
}

.multiselect:focus {
  outline: none;
}

.multiselect--disabled {
  pointer-events: none;
  opacity: 0.5;
}

.multiselect--active {
  z-index: 101;
}

.multiselect--active .multiselect__current,
.multiselect--active .multiselect__input,
.multiselect--active .multiselect__tags {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.multiselect--above.multiselect--active .multiselect__current,
.multiselect--above.multiselect--active .multiselect__input,
.multiselect--above.multiselect--active .multiselect__tags {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.multiselect__input {
  position: relative;
  border: none;
  width: calc(100%);
  transition: border 0.1s ease;
  box-sizing: border-box;
  height: 38px;
  text-overflow: ellipsis;
  display: inline-block;
}
.multiselect__input {
  background: #fff;
}
input[class*="multiselect__input"] {
  width: 100%;
  border: none;
}
.multiselect__tag ~ .multiselect__input,
.multiselect__tag ~ .multiselect__single {
  width: auto;
}

.multiselect__input:hover,
.multiselect__single:hover {
  border-color: #cfcfcf;
}

.multiselect__input:focus,
.multiselect__single:focus,
.multiselect__tags:focus {
  border: none;
  outline: none;
}

.multiselect__single {
  height: 38px;
  line-height: 38px;
  white-space: pre;
  overflow: hidden;
  padding-left: 10px;
  font-weight: normal;
  display: block;
}

.multiselect__tags-wrap {
  display: inline
}

.multiselect__tags {
  width: calc(100% - 40px);
  display: block;
  background: #fff;
}

.multiselect__tag {
  position: relative;
  display: inline-block;
  padding: 4px 26px 4px 10px;
  &.remove-disabled {
    padding-right: 10px;
  }
  margin-right: 2px;
  color: #fff;
  line-height: 1;
  background: #41B883;
  margin-bottom: 8px;
  white-space: nowrap;
}

.multiselect__tag-icon {
  cursor: pointer;
  margin-left: 7px;
  padding-top: 8px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: initial;
  width: 22px;
  text-align: center;
  transition: all 0.2s ease;
}

.multiselect__tag-icon:before {
  color: #495763;
  font-size: 14px;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: #369a6e;
}

.multiselect__tag-icon:focus:before,
.multiselect__tag-icon:hover:before {
  color: white;
}

.multiselect__current {
  line-height: 16px;
  min-height: 40px;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  padding: 8px 12px 0;
  padding-right: 30px;
  white-space: nowrap;
  margin: 0;
  text-decoration: none;
  border: 1px solid #E8E8E8;
  cursor: pointer;
}

.multiselect--active .multiselect__select {
  line-height: initial;
  transform: rotateZ(180deg);
}

.multiselect__select {
  display: block;
  position: absolute;
  box-sizing: border-box;
  width: 40px;
  height: 38px;
  line-height: 36px;
  right: 0;
  padding: 6px;
  margin: 0;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.multiselect__select:before {
  content: "\F107";
  display: inline-block;
  font: normal normal normal 14px/1 FontAwesome;
  font-size: inherit;
  text-rendering: auto;
  -webkit-font-smoothing: antialiased;
  font-size: 25px;
}

.multiselect__placeholder {
  color: #ADADAD;
  display: inline-block;
  margin-bottom: 10px;
  padding-top: 2px;
}

.multiselect--active .multiselect__placeholder {
  display: none;
}

.multiselect__content-wrapper {
  position: absolute;
  display: block;
  background: #fff;
  width: calc(100% + 2px);
  left: -1px;
  max-height: 240px;
  overflow: scroll;
  -ms-overflow-style: none;
  border-bottom: solid 1px #B5B5B5;
  /* border-top: none; */
  z-index: 50;
  -webkit-overflow-scrolling: touch;
}
.no-element-wrapper {
  width: auto;
}

.multiselect__content-wrapper::-webkit-scrollbar {
  display: none;
}

.multiselect__content {
  list-style: none;
  display: inline-block;
  padding: 0;
  margin: 0;
  min-width: 100%;
  max-width: 100%
}

.multiselect--above .multiselect__content-wrapper {
  bottom: 100%;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 1px solid #B5B5B5;
  border-top: 1px solid #B5B5B5;
}

.multiselect__content::webkit-scrollbar {
  display: none;
}

.multiselect__element {
  display: block;
  background: #fff;
  border: solid 1px #B5B5B5;
  border-top: solid 1px #EBEBEB;
  border-bottom: solid 1px #EBEBEB;

}
.multiselect__element:last-child {
  border-bottom: none;
}

.multiselect__option {
  display: block;
  padding: 10px 12px;
  min-height: 34px;
  line-height: 16px;
  text-decoration: none;
  text-transform: none;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.multiselect__option:after {
  top: 0;
  right: 0;
  position: absolute;
  line-height: 36px;
  padding-right: 12px;
  padding-left: 20px;
}

.multiselect__option--highlight {
  background: #6e7a91;
  outline: none;
  color: white;
}

.multiselect__option--highlight:after {
  content: attr(data-select);
  background: #6e7a91;
  color: white;
}

.multiselect__option--selected {
  background: #F3F3F3;
  color: #35495E;
  font-weight: normal;
}

.multiselect__option--selected:after {
  content: attr(data-selected);
  color: silver;
}

.multiselect__option--selected.multiselect__option--highlight {
  background: #6e7a91;
  color: #fff;
}

.multiselect__option--selected.multiselect__option--highlight:after {
  background: #6e7a91;
  content: attr(data-deselect);
  color: #fff;
}

.multiselect--disabled {
  background: #ededed;
  pointer-events: none;
}

.multiselect--disabled span.multiselect__tag{
  background: #e0e0e0 !important;
}

.multiselect--disabled span{
  color:#0C3A58;
}

.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__select,
.multiselect__tags input:disabled {
  background: #ededed;
  /* color: #a6a6a6; */
}

.multiselect__option--disabled {
  background: #ededed;
  color: #a6a6a6;
  cursor: text;
  pointer-events: none;
}

.multiselect__option--disabled.multiselect__option--highlight {
  background: #dedede !important;
}

.multiselect-enter-active,
.multiselect-leave-active {
  transition: all 0.15s ease;
}

.multiselect-enter,
.multiselect-leave-active {
  opacity: 0;
}

.multiselect__strong {
  margin-bottom: 10px;
  display: inline-block
}

@keyframes spinning {
  from { transform:rotate(0) }
  to { transform:rotate(2turn) }
}
</style>
<style lang="scss" scoped>
@import '~styles/multi-value';
</style>
